import { Box, Heading, HiddenVisually, Stack } from 'braid-design-system';

import { type Jobs } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { CompaniesList } from '../../components/companiesList/CompaniesList';
import { CompanyReviewAISummarySection } from '../../components/salaries/companyReviewAISummarySection/CompanyReviewAISummarySection';
import { OpenJobsSection } from '../../components/salaries/openJobsSection/OpenJobsSection';
import { SalariesListingSection } from '../../components/salaries/salariesListingSection/SalariesListingSection';
import { SalariesSearchSection } from '../../components/salaries/salariesSearchSection/SalariesSearchSection';
import { SalarySatisfactionSection } from '../../components/salaries/salarySatisfactionSection/SalarySatisfactionSection';
import translations from './.vocab';
import type { SalariesTabModel } from './salariesTabModel';

interface SalariesPageProps {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  model: SalariesTabModel;
  onActiveTabChanged: (item: string | undefined) => void;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
  trackRandomCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  trackReviewsClicked: () => void;
}

export const SalariesPage = ({
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
  trackRandomCompanyProfileLinkClicked,
  onActiveTabChanged,
  trackReviewsClicked,
}: SalariesPageProps) => {
  const { t } = useTranslations(translations);
  const { companyName } = model;

  return (
    <>
      <HiddenVisually>
        <Heading level="1">{t('Company salaries', { companyName })}</Heading>
      </HiddenVisually>
      <Box paddingX={['none', 'gutter']} paddingTop="large">
        <Stack space={'xlarge'}>
          <SalariesSearchSection model={model} />
          <SalariesListingSection model={model} />
          {!!model.salarySatisfactionRating && (
            <SalarySatisfactionSection model={model} />
          )}
          {model.jobs && (
            <OpenJobsSection
              model={model}
              getPaginatedJobs={getPaginatedJobs}
              trackJobsPaginationClicked={trackJobsPaginationClicked}
            />
          )}
          {model.companyReviewsAISummary && (
            <CompanyReviewAISummarySection
              model={model}
              onActiveTabChanged={onActiveTabChanged}
              trackReviewsClicked={trackReviewsClicked}
            />
          )}
          {model.randomCompaniesInThisIndustry &&
            model.randomCompaniesInThisIndustry.length !== 0 && (
              <CompaniesList
                title={t('Other companies in this industry')}
                allCompanies={model.randomCompaniesInThisIndustry}
                trackCompanyProfileLinkClicked={
                  trackRandomCompanyProfileLinkClicked
                }
              />
            )}
        </Stack>
      </Box>
    </>
  );
};
